import classNames from 'classnames';
import { floor } from 'lodash';
import React from 'react';

import { LocalizedMessage } from 'core/l10n/components';
import * as OvationsApi from 'core/ovations-api';
import { questionFieldIsDisabled, questionFieldIsRequired } from 'core/ovations-components/QuestionField';

import QuestionFieldProps from './definitions/QuestionFieldProps';

export type NumberQuestionProps = QuestionFieldProps<
    OvationsApi.Types.PromotionNumberQuestion,
    OvationsApi.Types.PromotionNumberQuestionAnswer
>;

interface NumberQuestionState {
    hasFocus: boolean;
}

class NumberQuestionField extends React.Component<NumberQuestionProps, NumberQuestionState> {
    constructor(props: NumberQuestionProps) {
        super(props);
        this.state = {
            hasFocus: false,
        };
    }

    onFocus = () => this.setState({ hasFocus: true });

    onBlur = () => this.setState({ hasFocus: false });

    onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.currentTarget;
        const { promotionQuestion } = this.props;
        const parsedValue = value ? floor(parseFloat(value), promotionQuestion.numberOfDecimalPlaces) : null;
        const answer: OvationsApi.Types.PromotionNumberQuestionAnswer = {
            type: OvationsApi.Enums.QuestionType.Number,
            value: parsedValue,
        };
        if (this.props.onChange) {
            this.props.onChange(answer);
        }
    };

    getDisplayedValue() {
        const { answer, promotionQuestion } = this.props;
        if (answer && answer.value) {
            if (this.state.hasFocus) {
                return answer.value;
            }
            return answer.value.toFixed(promotionQuestion.numberOfDecimalPlaces);
        }
        return '';
    }

    isUsedForQuantityRewardLimit(): boolean {
        const { isCallCenter, quantityRewardLimit, promotionQuestion } = this.props;
        if (
            isCallCenter &&
            quantityRewardLimit &&
            quantityRewardLimit.questionNumber === promotionQuestion.question.number
        ) {
            return true;
        }

        return false;
    }

    renderRangeConstraint(values: { min: number; max: number }) {
        return <LocalizedMessage id="questionForm_number_rangeConstraint" values={values} />;
    }

    renderMinimumConstraint(values: { min: number }) {
        return <LocalizedMessage id="questionForm_number_minimumConstraint" values={values} />;
    }

    renderMaximumConstraint(values: { max: number }) {
        return <LocalizedMessage id="questionForm_number_maximumConstraint" values={values} />;
    }

    renderValidationMessage() {
        const { minimumValue, maximumValue } = this.props.promotionQuestion;
        if (minimumValue) {
            return maximumValue
                ? this.renderRangeConstraint({ min: minimumValue, max: maximumValue })
                : this.renderMinimumConstraint({ min: minimumValue });
        }
        if (maximumValue) {
            return this.renderMaximumConstraint({ max: maximumValue });
        }
        return <LocalizedMessage id="questionForm_errorMessage_requiredField" />;
    }

    render() {
        const { inputProps = {}, promotionQuestion } = this.props;
        // eslint-disable-next-line no-restricted-properties
        const step = 10 ** (promotionQuestion.numberOfDecimalPlaces * -1 || 0);

        const shouldValidate = !(this.props.isCallCenter && !promotionQuestion.validateOnCallCenter);
        const validationProps = shouldValidate
            ? {
                  min: promotionQuestion.minimumValue || undefined,
                  max: promotionQuestion.maximumValue || undefined,
              }
            : {};

        return (
            <>
                <input
                    value={this.getDisplayedValue()}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    onChange={this.onChange}
                    name={promotionQuestion.id}
                    disabled={questionFieldIsDisabled(this.props)}
                    required={questionFieldIsRequired(this.props)}
                    type="number"
                    {...validationProps}
                    step={step}
                    {...inputProps}
                    className={classNames('form-control', inputProps.className, {
                        'border border-info': this.isUsedForQuantityRewardLimit(),
                    })}
                />
                <span className="invalid-feedback">{this.renderValidationMessage()}</span>
            </>
        );
    }
}

export default NumberQuestionField;
